/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file resultSlice.js
 * @description Redux slice for managing result related state and operations
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import originalAxios from "axios";

import createAxiosInstance from "../async/axios";
import { getRequestParams } from "../async/get-fetch-params";

import { sortByDescription } from "utils/sort/result-sort";

/**
 * @function getInitialState
 * @description Creates the initial state object for the result slice
 * @param {Object} params - Initial state parameters
 * @returns {Object} The initial state object
 */
export const getInitialState = (standardResults = null, mainBookResults = null, detailedEvent = null) => ({
  detailedEvent,
  error: null,
  isDetailedLoading: false,
  loading: false,
  mainBookResults,
  standardResults,
});

// Returns results per period...
let couponCancelToken;

/**
 * @function getStandardResultSlice
 * @description Async thunk for get standard result slice operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getStandardResultSlice = createAsyncThunk(
  "results/loadStandardResults",
  async ({ dateFrom, dateTo }, thunkAPI) => {
    try {
      const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());

      let thisCancelToken = null;
      // Check if there are any previous pending requests
      if (couponCancelToken) {
        // cancel the previous operation...
        couponCancelToken.cancel("Operation canceled due to new request.");
      }
      // Save the cancel token for the current request
      couponCancelToken = originalAxios.CancelToken.source();
      thisCancelToken = couponCancelToken;

      const axios = createAxiosInstance(thunkAPI.dispatch, {
        authToken,
        language,
      });
      const result = await axios.get(
        `/player/results?originId=${originId}&lineId=${lineId}&dateFrom=${dateFrom}&dateTo=${dateTo}`,
        { cancelToken: thisCancelToken.token },
      ); // Pass the cancel token to the current request

      const sortedBySport = {
        ...result.data,
        sports: result.data.sports.sort((a, b) => a.description.localeCompare(b.description)),
      };

      return { standardResults: sortedBySport };
    } catch (err) {
      const customError = {
        message: err.response?.headers["x-information"] || "Unable to obtain the result details", // serializable (err.response.data)
        name: "Result Load Error",
        status: err.response?.statusText,
      };
      throw customError;
    }
  },
);

/**
 * @function getMainBook
 * @description Async thunk for get main book operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getMainBook = createAsyncThunk("results/mainBook", async ({ dateFrom, dateTo }, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });
    const result = await axios.get(
      `/player/results/mainbook/default?originId=${originId}&lineId=${lineId}&dateFrom=${dateFrom}&dateTo=${dateTo}`,
    );

    // sort categories and tournaments inside of them by alphabet
    const sports = result.data.map((sport) => ({
      ...sport,
      categories: sortByDescription(sport.categories).map((category) => ({
        ...category,
        tournaments: sortByDescription(category.tournaments),
      })),
    }));

    return {
      mainBookResults: sports,
    };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to obtain the result details", // serializable (err.response.data)
      name: "Result Load Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @function getMainBookDetailedEvent
 * @description Async thunk for get main book detailed event operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getMainBookDetailedEvent = createAsyncThunk("results/mainBookDetailed", async ({ eventId }, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });
    const result = await axios.get(`/player/results/mainbook/${eventId}?originId=${originId}&lineId=${lineId}`);

    return { detailedEvent: result.data };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to obtain the result details", // serializable (err.response.data)
      name: "Result Load Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @constant resultSlice
 * @description Redux slice for result management
 */
const resultSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getStandardResultSlice.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(getStandardResultSlice.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(getStandardResultSlice.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
        state.standardResults = action.payload.standardResults;
      })
      .addCase(getMainBook.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.mainBookResults = null;
      })
      .addCase(getMainBook.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(getMainBook.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
        state.mainBookResults = action.payload.mainBookResults;
      })
      .addCase(getMainBookDetailedEvent.pending, (state) => {
        state.detailedEvent = null;
        state.error = null;
        state.isDetailedLoading = true;
      })
      .addCase(getMainBookDetailedEvent.rejected, (state, action) => {
        state.isDetailedLoading = false;
        state.error = action.error.message;
      })
      .addCase(getMainBookDetailedEvent.fulfilled, (state, action) => {
        state.detailedEvent = action.payload.detailedEvent;
        state.error = null;
        state.isDetailedLoading = false;
      });
  },
  initialState: getInitialState(),
  name: "results",
  reducers: {},
});
const { reducer } = resultSlice;
export default reducer;
